import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import styles from './HyphenSoftware.module.css';
import softwareVideo from '../../../images/hyphen.mp4';

const HyphenProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [hoveredService, setHoveredService] = useState(null);
  const [hoveredProcess, setHoveredProcess] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Service & Product Content Architecture', icon: '💡' },
    { text: 'UX Content Flow & Information Hierarchy', icon: '🎯' },
    { text: 'Technical Capability Translation', icon: '⚙️' },
    { text: 'Website Content Implementation', icon: '🌐' },
    { text: 'Value-Driven Messaging', icon: '📊' },
    { text: 'Brand & Visual Consistency', icon: '🎨' },
    { text: 'Continuous Content Evolution', icon: '🚀' }
  ];

  const tools = [
    { name: 'CMS / Static Site', icon: '📝', description: 'Content Management' },
    { name: 'Figma', icon: '🎨', description: 'UX Flows & Wireframes' },
    { name: 'Photoshop', icon: '🖼️', description: 'Visual Assets' },
    { name: 'Illustrator', icon: '✨', description: 'Icons & Vectors' },
    { name: 'Analytics', icon: '📊', description: 'Performance Review' }
  ];

  const skills = [
    'HTML5', 'CSS', 'Tailwind CSS', 'JavaScript', 'React', 'Node.js',
    'Cloud Architecture', 'Product Engineering', 'UX Content Design', 'SEO Optimization'
  ];

  

  

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Ongoing Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Integration Platform</h1>
                <p className={styles.projectSubtitle}>Product Engineering & Digital Solutions</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Content Strategy, Service Structuring, UX Content Flow</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2023 – Present</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>Ongoing</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                  An Integration Platform focused on enabling seamless connectivity between systems, applications, and workflows to build scalable and high-performance digital ecosystems. It supports organizations in developing, modernizing, and managing web platforms, mobile applications, SaaS products, and cloud-native solutions through unified integration capabilities.
                </p>
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                    className={styles.heroVideo}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src={softwareVideo} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                This platform bridges the gap between business needs and technical execution by integrating diverse systems into a cohesive environment. By combining modern engineering practices, product thinking, and agile methodologies, it enables organizations to streamline operations, improve data flow, and scale digital solutions efficiently. The focus is on building robust architectures, ensuring interoperability, and delivering maintainable, future-ready systems aligned with business growth.
              </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                My role involved shaping the platform’s digital narrative and ensuring clarity in how integration capabilities are communicated. This included organizing service offerings, defining content structure, and aligning technical features with clear business outcomes. I focused on making complex integration concepts accessible while improving usability, coherence, and overall communication across digital touchpoints.
              </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>

          
        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default HyphenProjectShowcase;